import { DestroyRef, Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ImpersonationService, VIEW_AS_HEADER } from './impersonation.service';

/** Header every API returns so a browser report and the server's own record
 *  of the same failure (with its stack trace) can be linked. */
export const CORRELATION_HEADER = 'X-Correlation-Id';

/** Where browser reports go. Calls to it are never themselves reported. */
export const ERROR_LOG_URL = `${environment.adminApiUrl}/api/errors/client`;

/** One failed API call as the browser saw it. No request/response body, no
 *  header values and no query-string values are ever included. */
export interface ClientErrorReport {
  eventId: string;
  occurredAt: string;          // when the call was made (UTC ISO)
  api: string;                 // admin | auth | benefit | ratefeed | premiumstudy | exchange | pdf | app | other
  method: string;
  path: string;                // endpoint path without query string
  queryKeys?: string[];        // names of the query parameters only
  status: number;              // 0 = network failure
  statusText?: string;
  errorType: 'HttpError' | 'NetworkError';
  message?: string;            // redacted, max 1,000 characters
  durationMs?: number;
  route: string;               // portal page, without query string
  correlationId?: string;
  occurrences: number;         // > 1 when repeats within 60 s were merged
}

export const MAX_REPORTS_PER_BATCH = 20;
export const MAX_REPORTS_PER_MINUTE = 30;
export const MERGE_WINDOW_MS = 60_000;
export const MAX_MESSAGE_LENGTH = 1000;
const MAX_ATTEMPTS = 3;

/** API base URLs → short service names, longest first so a base that is a
 *  prefix of another can't win by accident. */
const API_BASES: [string, string][] = ([
  [environment.adminApiUrl, 'admin'],
  [environment.authApiUrl, 'auth'],
  [environment.benefitApiUrl, 'benefit'],
  [environment.rateFeedApiUrl, 'ratefeed'],
  [environment.premiumStudyApiUrl, 'premiumstudy'],
  [environment.exchangeApiUrl, 'exchange'],
  [environment.disclosurePdfApiUrl, 'pdf'],
] as [string, string][]).filter(([base]) => !!base).sort((a, b) => b[0].length - a[0].length);

/**
 * The browser half of the error log (Beacon decision D8): every failed API
 * call the portal makes is reported to the Admin API, which stores it in a
 * separate error log — not the activity log — kept 30 days, Beacon admins only.
 *
 * Fed by the HTTP interceptor. Never throws, never blocks the caller.
 *
 * Noise controls: a repeat of the same method + path + status within 60 s is
 * not sent again; the repeats are counted and sent once as a single report
 * with `occurrences`. At most 30 reports leave a tab per minute. Nothing is
 * sent while signed out (there is no one to attribute it to).
 */
@Injectable({ providedIn: 'root' })
export class ErrorLogService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly impersonation = inject(ImpersonationService);

  private queue: { report: ClientErrorReport; attempts: number }[] = [];
  private flushScheduled = false;
  /** method|path|status → merge window for repeats. */
  private readonly recent = new Map<string, { until: number; repeats: number; sample: ClientErrorReport }>();
  private sentTimes: number[] = [];

  constructor() {
    if (typeof window !== 'undefined') {
      const onHide = () => this.flushOnUnload();
      window.addEventListener('pagehide', onHide);
      inject(DestroyRef).onDestroy(() => window.removeEventListener('pagehide', onHide));
    }
  }

  /** Report a failed call. `startedAt` is Date.now() when it was sent. */
  reportHttpError(req: HttpRequest<unknown>, err: HttpErrorResponse, startedAt: number): void {
    try {
      if (!this.auth.token()) return;
      if (req.url.startsWith(ERROR_LOG_URL)) return;   // never report our own reports
      this.enqueue(buildReport(req, err, startedAt));
    } catch {
      // Reporting must never break the call that failed.
    }
  }

  private enqueue(report: ClientErrorReport): void {
    const key = `${report.method}|${report.api}|${report.path}|${report.status}`;
    const now = Date.now();
    const open = this.recent.get(key);
    if (open && now < open.until) {
      open.repeats++;
      return;
    }
    this.recent.set(key, { until: now + MERGE_WINDOW_MS, repeats: 0, sample: report });
    setTimeout(() => this.closeWindow(key), MERGE_WINDOW_MS);
    this.push(report);
  }

  /** At the end of a merge window, send one report for the repeats it held. */
  private closeWindow(key: string): void {
    const open = this.recent.get(key);
    this.recent.delete(key);
    if (!open || open.repeats === 0) return;
    this.push({
      ...open.sample,
      eventId: newEventId(),
      occurredAt: new Date(open.until - MERGE_WINDOW_MS).toISOString(),
      durationMs: undefined,
      correlationId: undefined,
      occurrences: open.repeats,
    });
  }

  private push(report: ClientErrorReport): void {
    const now = Date.now();
    this.sentTimes = this.sentTimes.filter(t => now - t < 60_000);
    if (this.sentTimes.length >= MAX_REPORTS_PER_MINUTE) return;
    this.sentTimes.push(now);
    this.queue.push({ report, attempts: 0 });
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushScheduled) return;
    this.flushScheduled = true;
    setTimeout(() => {
      this.flushScheduled = false;
      this.flush();
    }, 0);
  }

  private flush(): void {
    while (this.queue.length) {
      const batch = this.queue.splice(0, MAX_REPORTS_PER_BATCH);
      this.http.post(ERROR_LOG_URL, { reports: batch.map(b => b.report) }).subscribe({
        error: (e: HttpErrorResponse) => {
          if (e.status >= 400 && e.status < 500) return;   // refused — don't retry
          const retry = batch.filter(b => ++b.attempts < MAX_ATTEMPTS);
          if (!retry.length) return;
          this.queue.unshift(...retry);
          setTimeout(() => this.scheduleFlush(), 2000 * retry[0].attempts);
        },
      });
    }
  }

  /** Tab closing: send what is queued, plus any repeats still being counted. */
  private flushOnUnload(): void {
    for (const key of [...this.recent.keys()]) this.closeWindow(key);
    if (!this.queue.length) return;
    const token = this.auth.token();
    if (!token || typeof fetch === 'undefined') return;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const viewed = this.impersonation.viewedUser();
    if (viewed) headers[VIEW_AS_HEADER] = String(viewed.id);
    while (this.queue.length) {
      const batch = this.queue.splice(0, MAX_REPORTS_PER_BATCH);
      try {
        void fetch(ERROR_LOG_URL, {
          method: 'POST', keepalive: true, headers,
          body: JSON.stringify({ reports: batch.map(b => b.report) }),
        }).catch(() => undefined);
      } catch { /* best effort */ }
    }
  }
}

// ── Building a report ────────────────────────────────────────────────────────

export function buildReport(req: HttpRequest<unknown>, err: HttpErrorResponse, startedAt: number): ClientErrorReport {
  const { api, path, queryKeys } = describeUrl(req.url);
  const keys = new Set([...queryKeys, ...req.params.keys()]);
  const network = !err.status;
  const correlationId = err.headers?.get(CORRELATION_HEADER) ?? undefined;
  return {
    eventId: newEventId(),
    occurredAt: new Date(startedAt).toISOString(),
    api,
    method: req.method,
    path,
    ...(keys.size ? { queryKeys: [...keys].sort() } : {}),
    status: err.status ?? 0,
    ...(err.statusText && err.statusText !== 'Unknown Error' ? { statusText: err.statusText } : {}),
    errorType: network ? 'NetworkError' : 'HttpError',
    message: network ? 'Network error - the server could not be reached.' : errorMessage(err),
    durationMs: Math.max(0, Date.now() - startedAt),
    route: typeof location !== 'undefined' ? location.pathname : '',
    ...(correlationId ? { correlationId } : {}),
    occurrences: 1,
  };
}

/** Which API, the endpoint path and the query-parameter names of a URL. */
export function describeUrl(url: string): { api: string; path: string; queryKeys: string[] } {
  const [beforeHash] = url.split('#');
  const [base, query = ''] = beforeHash.split('?');
  const queryKeys = query ? [...new URLSearchParams(query).keys()] : [];
  const match = API_BASES.find(([prefix]) => base.startsWith(prefix));
  if (match) return { api: match[1], path: base.slice(match[0].length) || '/', queryKeys };
  if (/^https?:\/\//i.test(base)) {
    try { return { api: 'other', path: new URL(base).pathname, queryKeys }; } catch { /* fall through */ }
  }
  return { api: 'app', path: base || '/', queryKeys };
}

/** The server's explanation, if it gave one, made safe to store. */
export function errorMessage(err: HttpErrorResponse): string | undefined {
  const body = err.error;
  const raw =
    typeof body === 'string' ? body
    : body && typeof body === 'object'
      ? (body.detail ?? body.message ?? body.title ?? body.error ?? null)
      : null;
  const text = typeof raw === 'string' ? raw : raw != null ? JSON.stringify(raw) : err.message;
  return text ? redactMessage(text) : undefined;
}

/**
 * Strips what must never reach the log from free text: bearer tokens and
 * JWTs, sensitive "key=value" / "key": "value" pairs, SSN-shaped and long
 * digit runs, and email addresses. Then trims to MAX_MESSAGE_LENGTH.
 */
export function redactMessage(text: string): string {
  const out = text
    .replace(/<[^>]+>/g, ' ')                                              // HTML error pages
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, '[token]')
    .replace(/("?(?:pass(?:word)?|token|secret|otp|code|ssn|dob|account(?:No|Number)?)"?\s*[:=]\s*)("[^"]*"|[^\s,;&}]+)/gi, '$1[redacted]')
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[redacted]')
    .replace(/\b\d{9,}\b/g, '[number]')
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/\s+/g, ' ')
    .trim();
  return out.length > MAX_MESSAGE_LENGTH ? out.slice(0, MAX_MESSAGE_LENGTH - 1) + '…' : out;
}

function newEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
